// client/src/lib/contextCapture/sanitizer.ts

/**
 * Sanitization patterns for removing sensitive data from captured context
 */

// Patterns that indicate sensitive data
const SENSITIVE_PATTERNS = [
  /password/i,
  /passwd/i,
  /secret/i,
  /token/i,
  /api[_-]?key/i,
  /auth/i,
  /bearer/i,
  /credential/i,
  /private[_-]?key/i,
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /session[_-]?id/i,
];

// Patterns for PII
const PII_PATTERNS = [
  /\b\d{4}[- ]?\d{4}[- ]?\d{4}[- ]?\d{4}\b/g,  // Credit card numbers
  /\b\d{3}-\d{2}-\d{4}\b/g,                      // SSN
  /\b\d{9}\b/g,                                   // SSN without dashes (9 consecutive digits)
];

// Headers that should always be redacted
const SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'x-access-token',
];

// Keys in objects that should be redacted
const SENSITIVE_KEYS = [
  'password',
  'token',
  'secret',
  'apiKey',
  'api_key',
  'authToken',
  'auth_token',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'privateKey',
  'private_key',
  'credential',
  'credentials',
  'ssn',
  'socialSecurityNumber',
  'creditCard',
  'credit_card',
  'cardNumber',
  'card_number',
  'cvv',
  'cvc',
];

const REDACTED = '[REDACTED]';

/**
 * Check if a key name suggests sensitive data
 */
export function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some(sk => lowerKey.includes(sk.toLowerCase())) ||
         SENSITIVE_PATTERNS.some(pattern => pattern.test(key));
}

/**
 * Check if a header is sensitive
 */
export function isSensitiveHeader(header: string): boolean {
  return SENSITIVE_HEADERS.includes(header.toLowerCase());
}

/**
 * Redact PII patterns from a string
 */
export function redactPII(text: string): string {
  let result = text;
  for (const pattern of PII_PATTERNS) {
    result = result.replace(pattern, REDACTED);
  }
  return result;
}

/**
 * Sanitize a URL by removing sensitive query parameters
 */
export function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url, window.location.origin);
    const params = urlObj.searchParams;

    // Check each param - collect keys first to avoid modifying during iteration
    const keysToRedact: string[] = [];
    params.forEach((_, key) => {
      if (isSensitiveKey(key)) {
        keysToRedact.push(key);
      }
    });
    keysToRedact.forEach(key => params.set(key, REDACTED));

    // A token under an innocent key or in the fragment (#access_token=...) is caught by shape/field name.
    return scrubTokenShapes(urlObj.toString());
  } catch {
    // If URL parsing fails, do basic redaction
    return redactPII(url);
  }
}

/**
 * Sanitize an object by redacting sensitive keys
 * Returns a new object (does not mutate input)
 */
export function sanitizeObject(obj: unknown, maxDepth = 5): unknown {
  if (maxDepth <= 0) return '[MAX_DEPTH]';

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    return redactPII(obj);
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, maxDepth - 1));
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (isSensitiveKey(key)) {
      result[key] = REDACTED;
    } else if (typeof value === 'string') {
      result[key] = redactPII(value);
    } else if (typeof value === 'object' && value !== null) {
      result[key] = sanitizeObject(value, maxDepth - 1);
    } else {
      result[key] = value;
    }
  }

  return result;
}

// Token SHAPES and credential FIELDS, wherever they appear in a string. Mirrors the server's redactSignInTokens
// (functions/src/issueTriage/redactSensitiveText.ts), deliberately a separate copy: the callable cannot trust the browser.
// No leading \b on the shapes: a token glued to a word or to `%3D` has no word boundary in front of it.
// Which HOSTS may keep their bodies at all is decided by isOwnBodyCaptureUrl (@officehub/shared), not here.
const CREDENTIAL_FIELD_NAMES =
  '(?:refresh|id|access|auth|oauth|bearer|custom)[_-]?token|client[_-]?secret|api[_-]?key|apikey|x-goog-api-key|' +
  'x-firebase-appcheck|password|passwd|secret|__session|session[_-]?(?:id|token)|set-cookie|cookie';
// `=`/`:`, their percent-encoded forms (%3D, %3A, double-encoded %253D), optionally with (escaped) quotes.
const FIELD_SEPARATOR = '(?:%(?:25)?3[DdAa]|\\\\*["\']?\\s*[:=]\\s*\\\\*["\']?)';
// A left boundary keeps `invalid_token` from being read as `id_token` (no lookbehind: older Safari lacks it).
const CREDENTIAL_FIELD = new RegExp(
  `(^|[^A-Za-z])(${CREDENTIAL_FIELD_NAMES})(${FIELD_SEPARATOR})(?!\\[REDACTED|%5BREDACTED)[^&"'\\s,}\\\\;]{8,}`,
  'gi'
);
// A bare `token` field: not after a letter, digit, `_` or `-` (so `invalid_token: expired_session` is left alone), and
// the value must look like a token (so `token: required` stays readable). Mirrors the server.
const BARE_TOKEN_FIELD = new RegExp(
  `(^|[^A-Za-z0-9_-])(token)(${FIELD_SEPARATOR})(?!\\[REDACTED|%5BREDACTED)` +
  '(?=[^&"\'\\s,}\\\\;]*[0-9._-]|[^&"\'\\s,}\\\\;]{20})[^&"\'\\s,}\\\\;]{8,}',
  'gi'
);
// camelCase compounds (`newRefreshToken`, `dbPassword`, `stripeApiKey`): case-SENSITIVE on purpose, the name follows a
// lowercase letter. Mirrors the server.
const CAMEL_CREDENTIAL_FIELD = new RegExp(
  `([a-z])((?:Refresh|Id|Access|Auth|OAuth|Bearer|Session|Custom)Token|ClientSecret|Secret|Password|Passwd|ApiKey|AccessKey)` +
  `(${FIELD_SEPARATOR})(?!\\[REDACTED|%5BREDACTED)[^&"'\\s,}\\\\;]{8,}`,
  'g'
);
// A whole Cookie / Set-Cookie value: its first pair can be short (`a=1; sid=SECRET`).
const COOKIE_HEADER = new RegExp(
  `(^|[^A-Za-z])((?:set-)?cookie)(${FIELD_SEPARATOR})(?!\\[REDACTED|%5BREDACTED)[^\\r\\n"']{8,}`,
  'gi'
);
// `Authorization: <scheme> <value>`: the value must look like a token, so "Authorization: required" stays readable.
const AUTHORIZATION = new RegExp(
  `(^|[^A-Za-z])(authorization)(${FIELD_SEPARATOR})\\s*(?:(?:basic|bearer|token|digest|negotiate)\\s+)?` +
  '(?!\\[REDACTED|%5BREDACTED)(?=[^&"\'\\s,}\\\\]*[0-9._-]|[^&"\'\\s,}\\\\]{20})[^&"\'\\s,}\\\\]{8,}',
  'gi'
);
const TOKEN_SHAPES: ReadonlyArray<readonly [RegExp, string]> = [
  [/AMf-vB[\w-]{20,}/g, REDACTED],                                       // Firebase refresh token
  [/ya29\.[\w-]{20,}|1(?:%2[Ff]|\/){2}0[\w-]{20,}/g, REDACTED],         // Google OAuth access / refresh (also %2F-encoded)
  [/AIza[\w-]{35}/g, REDACTED],                                          // Google API key
  // JWT (id token). Segments are BOUNDED: unbounded, a hostile "eyJ-eyJ-..." string is quadratic (~10 s at 200k chars).
  [/eyJ[\w-]{7,2048}\.[\w-]{8,2048}\.[\w-]{8,2048}/g, REDACTED],
  // Bearer: the value must look like a token (a digit or . _ ~ + / - in it, or 20+ characters).
  [/\bBearer\s+(?!\[REDACTED)(?=[\w.~+/=-]*[0-9._~+/-]|[\w.~+/=-]{20})[\w.~+/=-]+/gi, `Bearer ${REDACTED}`],
  [COOKIE_HEADER, `$1$2$3${REDACTED}`],
  [CREDENTIAL_FIELD, `$1$2$3${REDACTED}`],
  [CAMEL_CREDENTIAL_FIELD, `$1$2$3${REDACTED}`],
  [BARE_TOKEN_FIELD, `$1$2$3${REDACTED}`],
  [AUTHORIZATION, `$1$2$3 ${REDACTED}`],
  // A bare `token=` / `key=` in a URL query or form, token-looking values only.
  [/([?&](?:token|key)=)(?!\[REDACTED|%5BREDACTED)(?=[^&\s"']*[0-9._-]|[^&\s"']{20})[^&\s"']{8,}/gi, `$1${REDACTED}`],
  [/(<(?:refresh|id|access)[_-]?token>)(?!\[REDACTED|%5BREDACTED)[^<]{8,}(?=<)/gi, `$1${REDACTED}`],
];

/** Redact token-shaped values and credential fields from free text. */
export function scrubTokenShapes(text: string): string {
  let result = text;
  for (const [pattern, replacement] of TOKEN_SHAPES) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

/**
 * A form-encoded body (`a=1&b=2`, what securetoken.googleapis.com and OAuth endpoints take) is not JSON, so the
 * key-based redaction never ran on it. Redact the value of every sensitive key; keep the rest readable.
 * Returns null when the text does not look like a form body.
 */
function sanitizeFormEncoded(text: string): string | null {
  const trimmed = text.trim();
  // `key=value&key=value`: starts with a plain field name and `=`; not JSON, not markup. Values may hold spaces.
  if (!/^[\w.%+-]+=/.test(trimmed)) return null;
  return trimmed
    .split('&')
    .map((pair) => {
      const at = pair.indexOf('=');
      if (at < 0) return pair;
      const key = pair.slice(0, at);
      let decodedKey = key;
      try { decodedKey = decodeURIComponent(key); } catch { /* keep the raw key */ }
      return isSensitiveKey(decodedKey) ? `${key}=${REDACTED}` : pair;
    })
    .join('&');
}

/**
 * Sanitize a JSON string
 */
export function sanitizeJsonString(jsonStr: string, maxLength = 10000): string {
  // Scrub only what will be KEPT plus a margin (a token straddling the cut is still caught), never the whole body.
  const finish = (text: string): string => {
    const scrubbed = scrubTokenShapes(boundForScrub(text, maxLength));
    return scrubbed.length > maxLength ? scrubbed.slice(0, maxLength) + '...[TRUNCATED]' : scrubbed;
  };
  try {
    const obj = JSON.parse(jsonStr);
    return finish(JSON.stringify(sanitizeObject(obj)));
  } catch {
    // Not JSON: a form body gets key-based redaction, everything gets PII + token-shape redaction, then truncate
    const prefix = boundForScrub(jsonStr, maxLength);
    return finish(redactPII(sanitizeFormEncoded(prefix) ?? prefix));
  }
}

/** Characters scanned past the kept length, so a token that straddles the truncation point is still redacted. */
export const SCRUB_MARGIN = 256;

/**
 * The part of `text` worth scrubbing when only `maxLength` characters will be kept: the kept part plus a margin.
 * Everything past that is thrown away, so scanning it only costs CPU (and a hostile page can log megabytes).
 */
export function boundForScrub(text: string, maxLength: number): string {
  return text.length > maxLength + SCRUB_MARGIN ? text.slice(0, maxLength + SCRUB_MARGIN) : text;
}

/** Scrub token shapes in the part that will be kept, then truncate to `maxLength` (the order matters: scrub first). */
export function scrubThenTruncate(text: string, maxLength: number): string {
  return truncate(scrubTokenShapes(boundForScrub(text, maxLength)), maxLength);
}

/**
 * Sanitize request/response headers
 */
export function sanitizeHeaders(headers: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(headers)) {
    if (isSensitiveHeader(key)) {
      result[key] = REDACTED;
    } else {
      result[key] = redactPII(value);
    }
  }

  return result;
}

/**
 * Truncate a string to max length
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 13) + '...[TRUNCATED]';
}

// Contact details and credentials that can appear in a server's error body. The
// shared PII_PATTERNS above stop at card/SSN numbers; a function's 500 body can echo
// the email or phone it was processing, or a token it was handed (#3721).
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?1[\s.-]?)?\(?\b\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/g;
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
// Plain-text bodies never reach the JSON key sanitizer, so credentials written as
// `key=value` / `key: value`, and Google's two token shapes, are matched directly.
// The keyword may carry a NAME PREFIX (access_token, refresh_token, client_secret,
// id_token, sessionToken), so the boundary is a captured non-alphanumeric char or the
// start, not \b (which fails after "_" or a letter). Deliberately NOT a lookbehind:
// `(?<!...)` is a SyntaxError in Safari before 16.4, and a regex literal that fails to
// parse takes down the whole bundle on an older technician phone. The optional `"` before
// the separator covers a QUOTED key ("refresh_token": "…") in a body that is not valid
// JSON — e.g. truncated at the 8KB read bound — where the JSON key sanitizer is skipped.
const KEY_VALUE_PATTERN =
  /(^|[^A-Za-z0-9])([A-Za-z0-9_-]*?(?:token|secret|password|passwd|api[_-]?key|access[_-]?key))("?\s*[:=]\s*)("?)[^\s"',;&]+/gi;
const BASIC_AUTH_PATTERN = /\bBasic\s+[A-Za-z0-9+/=]{8,}/gi;
const GOOGLE_API_KEY_PATTERN = /AIza[0-9A-Za-z_-]{35}/g;
const GOOGLE_OAUTH_TOKEN_PATTERN = /ya29\.[0-9A-Za-z_-]+/g;
const TRUNCATED = '...[TRUNCATED]';

/**
 * Scrub a server response body before it is attached to an error report: the
 * existing JSON/key/PII sanitizer first, then emails, phone numbers, bearer
 * tokens, JWTs, Google API keys / OAuth tokens and plain-text `key=value`
 * credentials. The result is capped at `maxLength` characters INCLUDING the
 * truncation marker, so the cap is a hard bound.
 */
export function redactForErrorReport(text: string, maxLength = 1024): string {
  const redacted = sanitizeJsonString(text, maxLength * 8)
    .replace(JWT_PATTERN, REDACTED)
    .replace(GOOGLE_OAUTH_TOKEN_PATTERN, REDACTED)
    .replace(GOOGLE_API_KEY_PATTERN, REDACTED)
    .replace(BASIC_AUTH_PATTERN, `Basic ${REDACTED}`)
    .replace(KEY_VALUE_PATTERN, `$1$2$3$4${REDACTED}`)
    .replace(BEARER_PATTERN, `Bearer ${REDACTED}`)
    .replace(EMAIL_PATTERN, REDACTED)
    .replace(PHONE_PATTERN, REDACTED);
  if (redacted.length <= maxLength) return redacted;
  return redacted.slice(0, maxLength - TRUNCATED.length) + TRUNCATED;
}
